import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Theme,
  useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Styled from './SourcesPage.styles';
import * as actions from './actions';
import * as actionSource from '../SourcePage/actions';
import * as actionNetwork from '../NetworkPage/actions';
import Button from '../../components/Button';
import { selectors as sourceSelectors } from '../SourcePage/reducer';
import filterIcon from '../../global/media/filter.svg';
import { selectors } from './reducer';
import deleteIcon from '../../global/media/delete.svg';
import { getCities } from '../../global/cities';
import Source from './components/Source/Source';
import Loader from '../../components/Loader';
import { MappedTag, SourceData, TagsFieldResponse } from './types';
import SelectTags from './components/SelectTags';

type Sources = 'name' | 'code' | 'networkCode';

const SourcesPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [searchValue, setSearchValue] = useState<string>('');
  const [param, setParam] = useState<Sources>('name');
  const sources = useSelector(selectors.sourcesData);
  const disableReasons = useSelector(selectors.disableReasons);
  const allCities = getCities();
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [tegsSelected, setTagsSelected] = useState<boolean>(false);
  const [searchSelected, setSearchSelected] = useState<boolean>(false);

  const [selectedCitiesFilter, setSelectedCitiesFilter] = useState<string[]>([
    'Все',
  ]);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isOpenTagsOpen, setTagsOpen] = useState<boolean>(false);
  const [tagName, setTagName] = useState<
    { tag: string; color: string; id: string }[]
  >([]);
  const [name, setName] = useState<string[] | undefined>([]);
  const [settings, setSettings] = useState<
    { name: string; value: string; group: string }[]
  >([
    { name: 'Все', value: 'all', group: 'all' },
    { name: 'Видима', value: 'visible', group: 'visability' },
    { name: 'Невидима', value: 'invisible', group: 'visability' },
    {
      name: 'Синх более часа',
      value: 'synchronization',
      group: 'synchronization',
    },
    { name: 'Включена', value: 'on', group: 'switch' },
    { name: 'Выключена', value: 'off', group: 'switch' },
    { name: 'Активна', value: 'active', group: 'activating' },
    { name: 'Не активна', value: 'inactive', group: 'activating' },
  ]);
  const [selectedSettings, setSelectedSettings] = useState<string[]>(['all']);

  const sourceTagsData = useSelector(sourceSelectors.sourceTagsData);

  useEffect(() => {
    dispatch(actionSource.getSourceTags.request({ tagsType: 'source' }));
  }, []);

  useEffect(() => {
    dispatch(actions.getSources.request());
    dispatch(actionSource.clearSource());
    dispatch(actionNetwork.clearNetwork());
    dispatch(actionNetwork.clearSources());
    dispatch(actionSource.getSourceTags.request({ tagsType: 'source' }));
  }, [dispatch]);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  function getStyles(
    name: string,
    personName: readonly string[],
    theme: Theme
  ) {
    return {
      fontWeight: personName.includes(name)
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
    };
  }

  const theme = useTheme();

  const handleChangeCity = (event: any) => {
    const {
      target: { value },
    } = event;
    if (value.length === 0 || value.length === allCities.length) {
      setSelectedCitiesFilter(['Все']);
      return;
    }
    if (value.includes('Все')) {
      setSelectedCitiesFilter(
        value.filter((el: any) =>
          value[0] === 'Все' ? el !== 'Все' : el === 'Все'
        )
      );
      return;
    }
    setSelectedCitiesFilter(
      typeof value === 'string' ? value.split(',') : value
    );
  };

  const handleChangeSettings = (event: any) => {
    const { value } = event.target;

    if (value.length === 0) {
      setSelectedSettings(['all']);
      return;
    }
    if (value.includes('all')) {
      setSelectedSettings(
        value.filter((el: any) =>
          value[0] === 'all' ? el !== 'all' : el === 'all'
        )
      );
      return;
    }

    const groupMap = new Map<string, string>();
    value.forEach((v: any) => {
      const s = settings.find((s) => s.value === v);
      if (s) {
        if (groupMap.has(s.group)) {
          groupMap.delete(s.group);
        }
        groupMap.set(s.group, s.value);
      }
    });

    const result = Array.from(groupMap.values());
    setSelectedSettings(result);
  };

  const hour = 3600000;

  const newTags = (data: TagsFieldResponse[] | null): MappedTag[] => {
    return (
      data?.map((el) => ({
        tagName: el.name,
        tagColor: el.color || '',
        tagRequiredMeta: el.required_meta || false,
        tagId: el.id.toString(),
        allowed: el.allowed || false,
        isChecked: false,
      })) || []
    );
  };

  const mappedData = newTags(sourceTagsData);

  const handleTagCLick = (ids: string[]) => {
    const mappedTags = () => {
      return ids.map((e) => {
        const tag = mappedData?.find((el: { tagId: string }) => el.tagId === e);
        return {
          tag: tag?.tagName,
          color: tag?.tagColor,
          id: e,
        };
      });
    };
    if (ids) {
      setTagName(mappedTags as any);
    }
    setTagsOpen(false);
  };

  const updateCheckedTags = (
    mappedData: MappedTag[],
    selectedTags: string[]
  ): MappedTag[] => {
    return (
      mappedData &&
      mappedData.map((tag) => {
        if (selectedTags.includes(tag.tagId.toString())) {
          return { ...tag, isChecked: true };
        }
        return tag;
      })
    );
  };

  const updatedMappedData = updateCheckedTags(mappedData, selectedTags);

  const getSelectedTagNames = () => {
    const filteredTagNames =
      mappedData &&
      mappedData
        .filter((tag) => selectedTags.includes(tag.tagId))
        .map((tag) => tag.tagName);
    setName(filteredTagNames);
  };

  const handleResetTags = () => {
    selectedTags.length > 0 && setSelectedTags([]) && setTagsSelected(true);
    (name?.length || 0) > 0 && setName([]);
  };
  const filteredSource = useMemo(() => {
    if (!sources) return;

    return sources
      ?.filter((source: SourceData) =>
        selectedCitiesFilter.includes('Все')
          ? source
          : selectedCitiesFilter.includes(source.city)
      )
      .filter(
        (resource: SourceData) =>
          selectedTags.length === 0 ||
          resource.tags?.some((tag: any) =>
            selectedTags.includes(tag.tag_id.toString())
          )
      )
      .filter((pharmacy) => {
        if (selectedSettings.includes('visible') && !pharmacy.visible) {
          return false;
        }
        if (selectedSettings.includes('invisible') && pharmacy.visible) {
          return false;
        }
        if (selectedSettings.includes('on') && !pharmacy.activated) {
          return false;
        }
        if (selectedSettings.includes('off') && pharmacy.activated) {
          return false;
        }
        if (
          selectedSettings.includes('synchronization') &&
          !(
            pharmacy.lastSyncOrigin &&
            Date.now() - Date.parse(pharmacy.lastSyncOrigin) > hour &&
            Date.parse(pharmacy.lastSyncOrigin) !== -62135596800000
          )
        ) {
          return false;
        }
        if (selectedSettings.includes('active') && !pharmacy.active) {
          return false;
        }
        if (selectedSettings.includes('inactive') && pharmacy.active) {
          return false;
        }

        return true;
      })
      .filter((pharmacy) =>
        pharmacy[param as Sources]
          ?.toLocaleLowerCase()
          ?.includes(searchValue.toLocaleLowerCase())
      );
  }, [
    sources,
    selectedCitiesFilter,
    selectedTags,
    selectedSettings,
    param,
    searchValue,
  ]);

  useEffect(() => {
    setSearchSelected && setSearchSelected(false);
    tegsSelected && setTagsSelected(false);
  }, [searchSelected, tegsSelected]);

  useEffect(() => {
    dispatch(
      actions.getDisableReasons.request({
        key: 'disable_pharm_reasons',
      })
    );
  }, []);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setParam(event.target.value as Sources);
  };

  const handleToCreate = (source: boolean) => {
    if (source) {
      history.push(`/create#source`);
    } else {
      history.push(`/create#network`);
    }
  };

  const handleToPharmacyTags = () => {
    history.push('/pharmacy-tags');
  };

  const onContainerClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const actionEl = target.closest<HTMLElement>('[data-action]');

      if (!actionEl) return;
      const action = actionEl.getAttribute('data-action');
      const code = actionEl.getAttribute('data-code')!;

      switch (action) {
        case 'toggle':
          setExpandedCode((c) => (c === code ? null : code));
          break;
        case 'toSource':
          history.push(`/sources/${code}`);
          break;
        case 'toNetwork':
          history.push(`/networks/${code}`);
          break;
        default:
          break;
      }
    },
    [history]
  );

  interface LiteRowProps {
    source: SourceData;
  }

  const formatTimeAgo = (isoDate?: string): string => {
    if (!isoDate) return '—';

    const diffMs = Date.now() - Date.parse(isoDate);
    if (Number.isNaN(diffMs) || diffMs < 0) return '—';

    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return `${diffSec} сек назад`;
    if (diffMin < 60) return `${diffMin} мин назад`;
    if (diffHour < 24) {
      const restMin = diffMin % 60;
      return restMin > 0
        ? `${diffHour} ч ${restMin} мин назад`
        : `${diffHour} ч назад`;
    }
    return `${diffDay} дн назад`;
  };

  const LiteRow = React.memo<LiteRowProps>(
    ({ source }) => {
      const isOpen = expandedCode === source.code;
      const { offline } = source;
      const { online: isOnline = false } = offline ?? {};

      return (
        <Styled.LiteRow
          data-code={source.code}
          data-action="toggle"
          $isOpen={isOpen}
        >
          <Styled.SourceNameLink data-action="toSource" data-code={source.code}>
            {source.name}
          </Styled.SourceNameLink>

          <span
            data-action="toNetwork"
            data-code={source.networkCode}
            className="networkLink"
            style={{
              gridColumn: 2,
              gridRow: 1,
              justifySelf: 'start',
              marginLeft: '16px',
            }}
          >
            {source.networkName}
          </span>

          {offline && (
            <Styled.StatusRow>
              <Styled.StatusDot $online={isOnline} />
              <Styled.StatusText $online={isOnline}>
                {isOnline ? 'Online' : 'Offline'}
              </Styled.StatusText>
              <span>·</span>
              <span>v{offline.app_version}</span>
              <span>·</span>
              <span>{formatTimeAgo(offline.last_seen_at)}</span>
              <span>·</span>
              <span>{offline.ip_addresses?.join(', ')}</span>
            </Styled.StatusRow>
          )}

          {isOpen && (
            <Styled.SourceDetailsBlock>
              <Source data={source} disableReasons={disableReasons} />
            </Styled.SourceDetailsBlock>
          )}
        </Styled.LiteRow>
      );
    },
    (prev, next) =>
      prev.source.code === next.source.code &&
      prev.source.offline?.online === next.source.offline?.online &&
      prev.source.offline?.last_seen_at === next.source.offline?.last_seen_at
  );

  return (
    <>
      {!sources && <Loader />}
      <Styled.AdminPageContainer>
        <Styled.ButtonsBlock>
          <div className="add-network-btn">
            <Button
              text={t('SourcesPage.ADD_NETWORK')}
              onClick={() => handleToCreate(false)}
            />
          </div>
          <Button
            text={t('SourcesPage.ADD_SOURCE')}
            onClick={() => handleToCreate(true)}
            variant="outlined"
          />
          <div style={{ marginLeft: '15px' }}>
            <Button
              text={t('SourcesPage.ADD_TAGS')}
              onClick={() => handleToPharmacyTags()}
              variant="outlined"
            />
          </div>
        </Styled.ButtonsBlock>
        <div>
          <Styled.SearchContainer>
            <Styled.ContainerInput>
              <input
                autoComplete="off"
                value={searchValue}
                type="text"
                className="search-input"
                placeholder="Поиск аптек"
                onChange={handleSearch}
              />
              {searchValue && (
                <div
                  className="clear-input"
                  onClick={() => {
                    setSearchValue('');
                    setSearchSelected(true);
                  }}
                >
                  {t('SourcesPage.CLEAR')}
                </div>
              )}
            </Styled.ContainerInput>
            <Styled.ButtonSearch onClick={() => setSearchSelected(true)}>
              {t('SourcesPage.SEARCH')}
            </Styled.ButtonSearch>
          </Styled.SearchContainer>
          <Styled.ContainerRadioBox>
            <RadioGroup
              aria-labelledby="radio-buttons-group-label"
              defaultValue="name"
              row
              name="radio-buttons-group"
              onChange={handleChange}
            >
              <FormControlLabel value="name" control={<Radio />} label="name" />
              <FormControlLabel
                value="networkCode"
                control={<Radio />}
                label="network code"
              />
              <FormControlLabel value="code" control={<Radio />} label="code" />
            </RadioGroup>
          </Styled.ContainerRadioBox>
          <div
            style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}
          >
            <div>
              <FormControl sx={{ m: 1, width: 300 }}>
                <InputLabel id="demo-multiple-chip-label-city">
                  Город
                </InputLabel>
                <Select
                  labelId="demo-multiple-chip-label-city"
                  id="demo-multiple-chip-city"
                  multiple
                  value={selectedCitiesFilter}
                  onChange={handleChangeCity}
                  input={
                    <OutlinedInput
                      id="select-multiple-chip-city"
                      label="Chip-city"
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  <MenuItem
                    key="Все"
                    value="Все"
                    style={getStyles('Все', selectedCitiesFilter, theme)}
                  >
                    Все
                  </MenuItem>
                  {allCities.map((city: any) => (
                    <MenuItem
                      key={city.ruName}
                      value={city.ruName}
                      style={getStyles(
                        city.ruName,
                        selectedCitiesFilter,
                        theme
                      )}
                    >
                      {city.ruName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div>
              <FormControl sx={{ m: 1, width: 300 }}>
                <InputLabel id="demo-multiple-chip-label-settings">
                  Статус
                </InputLabel>
                <Select
                  labelId="demo-multiple-chip-label-settings"
                  id="demo-multiple-chip-settings"
                  multiple
                  value={selectedSettings}
                  onChange={handleChangeSettings}
                  input={
                    <OutlinedInput
                      id="select-multiple-chip-settings"
                      label="Chip-settings"
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={
                            settings.find((el) => el.value === value)?.name
                          }
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {settings.map((setting: any) => (
                    <MenuItem
                      key={setting.name}
                      value={setting.value}
                      style={getStyles(
                        setting.value,
                        selectedCitiesFilter,
                        theme
                      )}
                    >
                      {setting.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <Styled.FilterTags>
              <div className="tags-block">
                <div
                  className="set-tag"
                  onClick={() => setTagsOpen(!isOpenTagsOpen)}
                >
                  <div className="text">Теги</div>
                  <img src={filterIcon} alt="filter icon" />
                </div>
                {name && name.length > 0 && (
                  <div className="filter-info">
                    <span>
                      <span className="filter-by">Фильтр по: </span>
                      <span>{name?.map((el) => el).join(', ')}</span>

                      <span onClick={handleResetTags}>
                        <img src={deleteIcon} alt="delete icon" />
                      </span>
                    </span>
                  </div>
                )}
              </div>
              {isOpenTagsOpen && (
                <SelectTags
                  onClick={handleTagCLick}
                  onClose={() => setTagsOpen(false)}
                  data={updatedMappedData}
                  setData={() => {}}
                  isSource
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                  handleButtonClick={() => {
                    setTagsSelected(true);
                    getSelectedTagNames();
                  }}
                />
              )}
            </Styled.FilterTags>
          </div>
        </div>
        <div onClick={onContainerClick}>
          {filteredSource &&
            filteredSource?.map((source) => (
              <LiteRow key={source.code} source={source} />
            ))}
        </div>
      </Styled.AdminPageContainer>
    </>
  );
};

export default SourcesPage;
